<script setup lang="ts">
import { getFileUrl } from "~/services/assets";

const { t } = useI18n({ useScope: "local" });

interface PublicQRCode {
  id: string;
  code: string;
  title: string | null;
  description: string | null;
  organization_id: number;
  organization_name: string;
  organization_logo: string | null;
  organization_address: string | null;
  qr_code_url: string | null;
}

const props = defineProps<{ qr: PublicQRCode }>();

// Garde-fou anti « mixed content » : qr_code_url peut arriver en http:// interne
// Docker (donoor-directus-xxxx:8055) selon la config. getFileUrl extrait l'ID
// d'asset et reconstruit toujours l'URL publique HTTPS. Audit SEO 2026-08-02.
const safeQrUrl = computed(() =>
  props.qr.qr_code_url ? getFileUrl(props.qr.qr_code_url) : null,
);
</script>

<template>
  <a :href="`/donate/${qr.code}`" target="_blank" rel="noopener" class="group block h-full">
    <!-- Aucun effet au survol sur la carte (Karim 2026-08-08) : le seul repère
         interactif doit être le bouton, pas la carte entière. -->
    <div class="rounded-3xl border border-gray-200 h-full overflow-hidden bg-white p-4 flex flex-col">
      <div class="flex justify-center mb-3">
        <div class="w-28 h-28 bg-gray-50 rounded-xl p-2 border border-gray-100">
          <img
            v-if="safeQrUrl"
            :src="safeQrUrl"
            :alt="t('qrCard.alt', { nom: qr.organization_name })"
            class="w-full h-full object-contain"
            loading="lazy"
          />
          <img
            v-else
            :src="`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://donoor.org/pay/${qr.code}`"
            :alt="t('qrCard.alt', { nom: qr.organization_name })"
            class="w-full h-full object-contain"
            loading="lazy"
          />
        </div>
      </div>
      <div class="text-center flex-1 flex flex-col justify-center mb-3">
        <div class="flex justify-center mb-2">
          <OrgLogo
            :logo="qr.organization_logo"
            :name="qr.organization_name"
            size-class="w-10 h-10"
            rounded-class="rounded-full"
            ring-class="shadow-sm border border-gray-100"
          />
        </div>
        <h3 class="text-sm font-bold text-gray-900 line-clamp-1 leading-tight">
          {{ qr.organization_name }}
        </h3>
        <p v-if="qr.organization_address" class="text-gray-500 text-xs line-clamp-1 mt-0.5">
          <UIcon name="i-lucide-map-pin" class="size-3 inline-block -mt-0.5" />
          {{ qr.organization_address }}
        </p>
      </div>
      <!-- py-2.5 : bouton légèrement plus haut. Le survol vit ICI (hover:) et
           non plus sur la carte (group-hover:). -->
      <button class="w-full py-2.5 px-3 rounded-xl bg-main text-white text-xs font-semibold hover:bg-main/90 transition-colors">
        {{ t('qrCard.bouton') }}
      </button>
    </div>
  </a>
</template>

<i18n lang="json" src="../../../i18n/pages/components/recherche-page/RechercheQrCodeCard.json"></i18n>
